'use client';

import { useFormatter, useTranslations } from 'next-intl';
import { memo, useMemo } from 'react';
import { ActivityStatus } from '@/app/components/shared/Activity/ActivityStatus';
import type { Activity } from '@/app/types';
import { Link } from '@/i18n/routing';

interface Props {
  activity: Activity;
}

export const ActivityCard = memo(function ActivityCard({ activity }: Props) {
  const t = useTranslations();
  const format = useFormatter();

  const deadlineText = useMemo(() => {
    if (activity.phase !== 'in_submission' || !activity.end_time) return null;

    const endDate = new Date(activity.end_time);
    const formattedDate = format.dateTime(endDate, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    return t('activity.submissionDeadline', { date: formattedDate });
  }, [activity.phase, activity.end_time, format, t]);

  return (
    <Link
      href={`/activity/${activity.id}`}
      prefetch={false}
      className="group relative flex cursor-pointer flex-col overflow-hidden rounded-12"
    >
      <div className="relative size-full bg-system-white/5">
        <img
          src={activity.cover_uri}
          alt={activity.title || 'Vidu AI activity'}
          className="aspect-video w-full object-cover"
        />
        {deadlineText ? (
          <div className="absolute top-[10px] left-2 z-10 rounded-6 bg-system-black/50 px-2 py-1 text-white text-xs backdrop-blur-[20px] sm:hidden">
            {deadlineText}
          </div>
        ) : (
          <ActivityStatus
            status={activity.phase}
            className="absolute top-[10px] left-2 z-10 rounded-6 bg-system-black/50 text-white backdrop-blur-[20px] sm:hidden"
          />
        )}
        <div className="flex h-[82px] w-full flex-col gap-2 p-4 sm:h-8 sm:p-2">
          <h3 className="line-clamp-1 font-medium text-base text-white leading-[1.4] sm:text-xs">{activity.title}</h3>
          <p className="line-clamp-1 text-sm text-white/70 leading-[1.4] sm:hidden">{activity.subtitle}</p>
        </div>
      </div>
    </Link>
  );
});
